import React, { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import Nav from "@/components/Nav";
import { CATEGORIES, CATEGORY_BY_SLUG } from "@/data/work";

const EASE = [0.22, 1, 0.36, 1];

// "On-ground Activations" -> "On-Ground Activations"
const titleCase = (s) =>
  s.replace(/(^|[\s-])([a-z])/g, (m, p, c) => p + c.toUpperCase());

export default function WorkCategory() {
  const { slug } = useParams();
  const category = CATEGORY_BY_SLUG[slug];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!category) return <Navigate to="/" replace />;

  const idx = CATEGORIES.findIndex((c) => c.slug === slug);
  const nextCat = CATEGORIES[(idx + 1) % CATEGORIES.length];

  return (
    <div
      data-testid="work-category-page"
      className="bg-[color:var(--collab-cream)]"
    >
      <Nav />
      <main className="pt-28 md:pt-36 pb-20">
        {/* Category hero — compact, centred */}
        <section className="shell">
          <div className="flex flex-col items-center gap-4 sm:flex-row sm:items-center sm:justify-between">
            <span className="label-mono text-center sm:text-left">
              [ Work / {category.label.toUpperCase()} ]
            </span>
            <Link
              to="/#team"
              className="label-mono hover:text-[color:var(--collab-forest)] transition-colors"
              data-testid="back-all-work"
            >
              ← All Work
            </Link>
          </div>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: EASE }}
            className="mt-10 md:mt-14 text-center font-display font-light text-4xl md:text-6xl leading-[1.05] tracking-[-0.02em] text-[color:var(--collab-ink)]"
          >
            {titleCase(category.label)}
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: EASE, delay: 0.15 }}
            className="mt-4 md:mt-5 mx-auto text-center font-display italic text-2xl md:text-[1.8rem] md:whitespace-nowrap leading-[1.3] text-[color:var(--collab-forest)] pretty"
          >
            {category.tagline}
          </motion.p>
        </section>

        {/* Events grid — 3 per row */}
        <section
          className="shell mt-10 md:mt-14"
          data-testid="projects-section"
        >
          {category.events.length === 0 ? (
            <div className="text-center py-16 md:py-24 border-t border-b border-[color:var(--collab-hairline)]">
              <span className="label-mono">Selected projects coming soon</span>
              <p className="mt-6 font-display italic text-2xl md:text-3xl text-[color:var(--collab-forest)] max-w-2xl mx-auto balance">
                We're preparing the case book for this category — check back
                shortly, or reach out to see the private edition.
              </p>
            </div>
          ) : (
            <div className="border-t border-[color:var(--collab-hairline)] pt-12 md:pt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 md:gap-x-8 gap-y-14 md:gap-y-16">
              {/* repeat duplicates the events to preview additional rows */}
              {(category.repeat
                ? Array(category.repeat + 1).fill(category.events).flat()
                : category.events
              ).map((e, i) => (
                <EventCard
                  key={`${e.id}-${i}`}
                  event={e}
                  index={i}
                  metaTall={!!category.longMeta}
                  slug={category.slug}
                />
              ))}
            </div>
          )}
        </section>

        {/* Next category */}
        <section className="shell mt-20 md:mt-28">
          <div className="border-t border-[color:var(--collab-hairline)] pt-10 md:pt-12 grid grid-cols-1 md:grid-cols-2 gap-10 items-end">
            <Link
              to="/#team"
              className="label-mono hover:text-[color:var(--collab-forest)] transition-colors"
              data-testid="footer-all-work"
            >
              ← All Work
            </Link>
            <Link
              to={`/work/${nextCat.slug}`}
              className="group text-right"
              data-testid="next-category"
            >
              <span className="label-mono block">Next</span>
              <span className="mt-3 font-display text-3xl md:text-5xl leading-[1.05] tracking-[-0.02em] text-[color:var(--collab-ink)] block transition-transform duration-500 group-hover:-translate-x-1">
                {nextCat.label}{" "}
                <span
                  aria-hidden
                  className="inline-block transition-transform duration-500 group-hover:translate-x-2"
                >
                  →
                </span>
              </span>
            </Link>
          </div>
        </section>

        {/* CTA */}
        <section className="shell mt-16 md:mt-24">
          <div className="border-t border-[color:var(--collab-hairline)] pt-10 md:pt-14 text-center">
            <p className="font-display font-light text-3xl md:text-5xl leading-[1.1] tracking-[-0.02em] text-[color:var(--collab-ink)] balance">
              Have an experience in mind?
            </p>
            <p className="mt-3 font-display italic text-2xl md:text-4xl text-[color:var(--collab-forest)]">
              Let's create something people remember.
            </p>
            <Link
              to="/#contact"
              data-testid="category-cta-start"
              className="mt-8 inline-flex btn-forest"
            >
              Start a project
              <span aria-hidden>→</span>
            </Link>
          </div>
        </section>
      </main>
    </div>
  );
}

function EventCard({ event, index, metaTall, slug }) {
  const linked = !!event.detail;
  const inner = (
    <>
      {/* Brand logo — fixed-height row keeps titles aligned across cards */}
      <div className="h-28 md:h-32 flex items-center justify-center">
        <img
          src={event.logo}
          alt={event.logoAlt}
          className={`${event.logoClass || "h-7 md:h-8"} w-auto object-contain`}
          data-testid={`event-logo-${index}`}
        />
      </div>

      {/* Event name + location/type — fixed 2-line reserves, top-aligned,
          keep every heading level and image in a row perfectly aligned */}
      <h3 className="mt-4 min-h-[2.3em] flex items-start justify-center font-display font-light text-2xl md:text-[1.7rem] leading-[1.15] tracking-[-0.01em] text-[color:var(--collab-ink)] balance">
        {event.title}
      </h3>
      {event.meta && (
        <p
          className={`mt-2 label-mono !text-[color:var(--collab-muted)] ${
            metaTall ? "min-h-[6.6em]" : "min-h-[3.3em]"
          }`}
        >
          {event.meta}
        </p>
      )}

      {/* Event image — uniform square tile */}
      {event.image && (
        <div className="mt-5 md:mt-6 relative w-full aspect-square overflow-hidden bg-[color:var(--collab-forest-deep)]">
          <img
            src={event.image.src}
            alt={event.image.alt}
            className={`absolute inset-0 w-full h-full object-cover ${
              event.image.pos || "object-center"
            } scale-[1.02] transition-transform duration-[900ms] ease-out group-hover:scale-105`}
            loading="lazy"
            data-testid={`event-image-${index}`}
          />
        </div>
      )}

      {/* Body copy */}
      {event.copy && (
        <p className="mt-5 text-sm md:text-base leading-relaxed text-[color:var(--collab-muted)] pretty">
          {event.copy}
        </p>
      )}
    </>
  );
  return (
    <motion.article
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.9, ease: EASE, delay: (index % 3) * 0.09 }}
      className="group flex flex-col items-center text-center"
      data-testid={`event-card-${index}`}
    >
      {linked ? (
        <Link
          to={`/work/${slug}/${event.id}`}
          data-testid={`event-card-link-${index}`}
          className="flex flex-col items-center text-center w-full cursor-pointer"
        >
          {inner}
        </Link>
      ) : (
        inner
      )}
    </motion.article>
  );
}
